// Helpers for displaying saved Routes in ML sheet creation.

/**
 * Build a readable label for a route option:
 *   RT1-ATHN-26-27 · Mumbai → Ratnagiri → Goa
 */
export const formatRouteLabel = (route) => {
  if (!route) return '';
  const parts = [
    route.from_location?.location,
    ...((route.via_locations || []).map((v) => v.location)),
    route.to_location?.location,
  ].filter(Boolean);
  const path = parts.join(' → ');
  const name = (route.name || '').trim();
  const idPart = route.route_id || '';
  const head = [name, idPart].filter(Boolean).join(' · ');
  if (head && path) return `${head} · ${path}`;
  return head || path;
};

/**
 * Ordered unload stops for a route: via hubs + final destination.
 * Falls back to backend-provided `destination_sequence` when present.
 */
export const routeDestinationSequence = (route) => {
  if (!route) return [];
  if (Array.isArray(route.destination_sequence) && route.destination_sequence.length) {
    return route.destination_sequence;
  }
  const via = (route.via_location_ids || []).filter((x) => x != null);
  return route.to_location_hub_id != null ? [...via, route.to_location_hub_id] : via;
};

/** Travel path hub ids: origin → via stops → final destination (unique, in order). */
export const routePathHubIds = (route) => {
  if (!route) return [];
  const path = [];
  const add = (raw) => {
    const id = Number(raw);
    if (Number.isNaN(id) || path.includes(id)) return;
    path.push(id);
  };
  add(route.from_location_hub_id);
  (route.via_location_ids || []).forEach(add);
  routeDestinationSequence(route).forEach(add);
  return path;
};

/** Map location hub id → parent hub id (branches share travel with their hub). */
export const buildLocationHubParentMap = (locations = []) => {
  const map = {};
  (locations || []).forEach((loc) => {
    const id = Number(loc.id ?? loc.hub_id);
    if (Number.isNaN(id)) return;
    const parentRaw = loc.parent_hub_id;
    map[id] = parentRaw != null && parentRaw !== '' ? Number(parentRaw) : id;
  });
  return map;
};

export const hubMatchesRoutePoint = (selectedId, pointId, parentMap = null) => {
  const sid = Number(selectedId);
  const point = Number(pointId);
  if (Number.isNaN(sid) || Number.isNaN(point)) return false;
  if (sid === point) return true;
  if (!parentMap || !Object.keys(parentMap).length) return false;
  const sp = parentMap[sid] ?? sid;
  const pp = parentMap[point] ?? point;
  return sp === pp || sp === point || sid === pp;
};

/** Crossing destinations rank just after their crossing hub, before the next stop. */
const CROSSING_DEST_STEP = 0.001;

const indexOnPath = (path, rawId, parentMap) => {
  const id = Number(rawId);
  if (Number.isNaN(id)) return null;
  // A stop that is itself on the path keeps that position. Parent matching
  // is only for a branch that is not listed as its own stop. Otherwise every
  // city under the origin (Tirupati, Nellore) collapses onto the origin and
  // the route has no forward destination.
  for (let i = 0; i < path.length; i += 1) {
    if (Number(path[i]) === id) return i;
  }
  for (let i = 0; i < path.length; i += 1) {
    if (hubMatchesRoutePoint(id, path[i], parentMap)) return i;
  }
  return null;
};

/**
 * Crossing hubs are via points. A hub already on the path keeps that index.
 * A hub that is only a crossing location sits just after the origin.
 */
const crossingPositions = (route, path, parentMap) => {
  const stops = (route?.crossing_stops || [])
    .map((stop) => ({ ...stop, hub_id: Number(stop.hub_id) }))
    .filter((stop) => !Number.isNaN(stop.hub_id));
  const offPath = stops.filter((stop) => indexOnPath(path, stop.hub_id, parentMap) == null);
  const offIndex = new Map();
  offPath.forEach((stop, index) => {
    offIndex.set(stop.hub_id, ((index + 1) / (offPath.length + 1)) * 0.5);
  });
  return stops
    .map((stop) => {
      const onPath = indexOnPath(path, stop.hub_id, parentMap);
      const idx = onPath != null ? onPath : offIndex.get(stop.hub_id);
      return { idx, stop };
    })
    .filter((row) => row.idx != null);
};

/** Position along the route (main path + crossing hubs treated as via points). */
export const travelIndexOnRoute = (route, hubId, parentMap = null) => {
  if (!route || hubId == null || hubId === '') return null;
  const path = routePathHubIds(route);
  const direct = indexOnPath(path, hubId, parentMap);
  if (direct != null) return direct;

  for (const { idx, stop } of crossingPositions(route, path, parentMap)) {
    if (hubMatchesRoutePoint(hubId, stop.hub_id, parentMap)) return idx;
    const destIds = [
      ...(stop.destination_hub_ids || []),
      ...((stop.destinations || []).map((d) => d.id).filter((x) => x != null)),
    ];
    if (destIds.some((destId) => hubMatchesRoutePoint(hubId, destId, parentMap))) {
      return idx + CROSSING_DEST_STEP;
    }
  }
  return null;
};

/** True when this hub is a city goods cross to from one of the route's crossing hubs. */
export const isCrossingDestination = (route, hubId, parentMap = null) => {
  if (!route || hubId == null || hubId === '') return false;
  return (route.crossing_stops || []).some((stop) => {
    const destIds = [
      ...(stop.destination_hub_ids || []),
      ...((stop.destinations || []).map((d) => d.id).filter((x) => x != null)),
    ];
    return destIds.some((destId) => hubMatchesRoutePoint(hubId, destId, parentMap));
  });
};

const hubsOnRouteTravel = (route, hubIds, parentMap = null) => {
  if (!hubIds?.length) return true;
  return hubIds.every((id) => travelIndexOnRoute(route, id, parentMap) != null);
};

/** From/To selections must lie on the route and move forward along the path. */
export const routeMatchesMlFromTo = (route, fromIds, toIds, parentMap = null) => {
  if (!route) return false;
  const indexOf = (hubId) => travelIndexOnRoute(route, hubId, parentMap);

  const fromList = (fromIds || []).length
    ? fromIds
    : (route.from_location_hub_id != null ? [route.from_location_hub_id] : []);
  if (!fromList.length) return true;

  let maxFrom = -1;
  for (const raw of fromList) {
    const idx = indexOf(raw);
    if (idx == null) return false;
    maxFrom = Math.max(maxFrom, idx);
  }

  if (!(toIds || []).length) return true;

  let minTo = Number.MAX_SAFE_INTEGER;
  for (const raw of toIds) {
    const idx = indexOf(raw);
    if (idx == null) return false;
    minTo = Math.min(minTo, idx);
  }
  return maxFrom < minTo;
};

export const ML_ROUTE_CUSTOM_LABEL = 'Custom — select locations manually';

/** Hint under route dropdown when the list has no saved corridors or search is too narrow. */
/** True when every shipment’s sender/receiver hub is on the ML sheet from/to lists. */
export const mlSheetCoversShipments = (sheet, shipments = []) => {
  if (!sheet || !shipments.length) return false;
  const fromSet = new Set((sheet.from_location_ids || []).map((id) => String(id)));
  const toSet = new Set((sheet.to_location_ids || []).map((id) => String(id)));
  return shipments.every((s) => {
    const fromOk = !s.sender_location_hub_id || fromSet.has(String(s.sender_location_hub_id));
    const toOk = !s.receiver_location_hub_id || toSet.has(String(s.receiver_location_hub_id));
    return fromOk && toOk;
  });
};

export const mlRouteDropdownEmptyMessage = (searchTerm, hasCorridorRoutes, hasAnyRoutes) => {
  const q = (searchTerm || '').trim();
  if (q) {
    return 'No routes match your search. Clear the search or choose Custom.';
  }
  if (!hasAnyRoutes) {
    return 'No saved routes in the system. Use Custom and select From/To manually.';
  }
  if (!hasCorridorRoutes) {
    return '';
  }
  return '';
};

/** Pick the best saved route for the current From/To (client-side). */
export const pickBestCorridorRouteForMlForm = (
  routes,
  fromIds,
  toIds,
  parentMap = null,
) => {
  const matches = corridorRoutesForMlForm(routes, {
    fromIds,
    toIds,
    parentMap,
  });
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0];

  const scored = matches.map((route) => {
    let score = 0;
    (fromIds || []).forEach((id) => {
      if (hubMatchesRoutePoint(id, route.from_location_hub_id, parentMap)) score += 100;
    });
    (toIds || []).forEach((id) => {
      if (hubMatchesRoutePoint(id, route.to_location_hub_id, parentMap)) score += 50;
      const seq = routeDestinationSequence(route);
      if (seq.some((hub) => hubMatchesRoutePoint(id, hub, parentMap))) score += 10;
    });
    return { route, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0].route;
};

/** Routes whose path covers the selected From/To in order (strict corridor match). */
export const corridorRoutesForMlForm = (
  routes,
  { fromIds = [], toIds = [], alwaysIncludeRouteId = null, parentMap = null } = {},
) => {
  const all = Array.isArray(routes) ? routes : [];
  if (!fromIds.length && !toIds.length) {
    return all;
  }
  let list = all.filter(
    (route) =>
      hubsOnRouteTravel(route, fromIds, parentMap)
      && hubsOnRouteTravel(route, toIds, parentMap)
      && routeMatchesMlFromTo(route, fromIds, toIds, parentMap),
  );
  if (alwaysIncludeRouteId != null && alwaysIncludeRouteId !== '') {
    const selected = all.find((r) => String(r.id) === String(alwaysIncludeRouteId));
    if (selected && !list.some((r) => String(r.id) === String(selected.id))) {
      list = [selected, ...list];
    }
  }
  return list;
};

/**
 * Route dropdown list for ML create/edit/QR.
 * The route that matches the current From/To stays first.
 * Every other route that includes this From stays in the list, so the user
 * can switch from Nellore to Vijayawada without clearing the form.
 */
export const filterRoutesForMlForm = (
  routes,
  { fromIds = [], toIds = [], searchTerm = '', alwaysIncludeRouteId = null, parentMap = null } = {},
) => {
  const all = Array.isArray(routes) ? routes : [];
  const corridor = corridorRoutesForMlForm(all, {
    fromIds,
    toIds,
    alwaysIncludeRouteId,
    parentMap,
  });
  const fromRoutes = fromIds.length
    ? all.filter((route) => hubsOnRouteTravel(route, fromIds, parentMap))
    : all;

  const seen = new Set();
  const list = [];
  const push = (route) => {
    if (!route) return;
    const key = String(route.id);
    if (seen.has(key)) return;
    seen.add(key);
    list.push(route);
  };
  corridor.forEach(push);
  (fromRoutes.length ? fromRoutes : all).forEach(push);
  if (alwaysIncludeRouteId != null && alwaysIncludeRouteId !== '') {
    push(all.find((route) => String(route.id) === String(alwaysIncludeRouteId)));
  }
  if (!list.length) all.forEach(push);

  const q = (searchTerm || '').toLowerCase().trim();
  if (!q) return list;
  return list.filter((route) => {
    const label = formatRouteLabel(route).toLowerCase();
    const name = String(route.name || '').toLowerCase();
    const code = String(route.route_id || '').toLowerCase();
    return label.includes(q) || name.includes(q) || code.includes(q);
  });
};

/** Apply /api/routes/match result onto ML form fields (route + crossing from route). */
export const mlFormFieldsFromMatchedRoute = (prev, matched) => {
  if (!matched?.id) {
    return {
      ...prev,
      route_id: '',
      ...(prev.crossingStopsFromRoute
        ? {
          crossing_stops: [],
          crossing_location_hub_ids: [],
          crossing_destination_hub_ids: [],
          crossingStopsFromRoute: false,
        }
        : {}),
    };
  }
  const picked = crossingSelectionFromRoute(matched);
  return {
    ...prev,
    route_id: matched.id,
    ...(picked.crossing_stops.length
      ? {
        crossing_stops: picked.crossing_stops,
        crossing_location_hub_ids: picked.crossing_location_hub_ids,
        crossing_destination_hub_ids: picked.crossing_destination_hub_ids,
        crossingStopsFromRoute: true,
      }
      : prev.crossingStopsFromRoute
        ? {
          crossing_stops: [],
          crossing_location_hub_ids: [],
          crossing_destination_hub_ids: [],
          crossingStopsFromRoute: false,
        }
        : {}),
  };
};

/** Pairs saved on a route, ready to copy onto an ML sheet. */
export const crossingSelectionFromRoute = (route) => {
  const stops = (Array.isArray(route?.crossing_stops) ? route.crossing_stops : [])
    .map((stop) => ({
      hub_id: Number(stop.hub_id),
      hub_name: stop.hub_name || stop.hub?.location || '',
      destination_hub_ids: (stop.destination_hub_ids || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id)),
      destinations: stop.destinations || [],
    }))
    .filter((stop) => !Number.isNaN(stop.hub_id) && stop.destination_hub_ids.length);
  const locationIds = [...new Set(stops.map((stop) => Number(stop.hub_id)))];
  const destinationIds = [...new Set(stops.flatMap((stop) => stop.destination_hub_ids))];
  return {
    crossing_stops: stops,
    crossing_location_hub_ids: locationIds,
    crossing_destination_hub_ids: destinationIds,
  };
};

/** Manual ML selection: every chosen hub shares the same destinations. */
export const crossingStopsFromFlat = (locationIds, destinationIds) => (
  (locationIds || []).map((id) => ({
    hub_id: Number(id),
    destination_hub_ids: (destinationIds || []).map((dest) => Number(dest)),
  }))
);

export const formatCrossingStopSummary = (stops) => {
  if (!stops?.length) return '';
  return stops.map((stop) => {
    const hub = stop.hub_name || `Hub ${stop.hub_id}`;
    const named = (stop.destinations || []).map((dest) => dest.location).filter(Boolean);
    const destLabel = named.length ? named.join(', ') : (stop.destination_hub_ids || []).join(', ');
    return `${hub} → ${destLabel}`;
  }).join(' · ');
};

/** Resolve API match or fall back to loaded routes + hub/branch map. */
export const resolveMatchedRouteForMlForm = async (
  routeService,
  { routes, fromIds, toIds, parentMap },
) => {
  if (!fromIds?.length || !toIds?.length) return null;
  let apiRoute = null;
  try {
    const res = await routeService.match(fromIds, toIds);
    if (res?.data?.id) apiRoute = res.data;
  } catch {
    apiRoute = null;
  }
  if (apiRoute?.id) return apiRoute;
  return pickBestCorridorRouteForMlForm(routes, fromIds, toIds, parentMap);
};

export const ML_ROUTE_LOCATION_MISMATCH =
  'These From/To locations are not on the selected route. Adjust locations or choose Custom.';
